import { apiClient } from '../client';
import { dineEndpoints } from '../endpoints/dine.endpoints';
import { call } from './unwrap';
import { hydrateOrderDetail, hydrateOrderSummary, hydrateSendResult } from '../hydrate';
import type {
  AddLineRequest,
  ChargeLineRequest,
  OpenItemRequest,
  OpenOrderRequest,
  OrderDetailDto,
  OrderSummaryDto,
  OrdersQuery,
  PendingApprovalDto,
  RequestBillRequest,
  ReturnLineRequest,
  SeatingRequest,
  SendResultDto,
  UpdateLineRequest,
} from '../types/dine.types';

/*
 * One function per contract endpoint. Every mutation returns the full
 * OrderDetailDto (so the client never needs a second round trip); `send`
 * returns SendResultDto. Errors are Error(serverMessage) with `status`.
 * Every return path is hydrated (src/api/hydrate.ts): the API omits null
 * fields, the DTOs promise `null`.
 */
const detail = (p: Promise<OrderDetailDto>) => p.then(hydrateOrderDetail);

export const orderService = {
  /** GET /orders?status=&sort=&mine= (array) */
  list(q: OrdersQuery = {}): Promise<OrderSummaryDto[]> {
    return call<OrderSummaryDto[]>(apiClient.get(dineEndpoints.orders(q)), 'Could not load orders').then((rows) =>
      rows.map(hydrateOrderSummary),
    );
  },

  /** GET /orders/{id} */
  get(id: string): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.get(dineEndpoints.order(id)), 'Could not load the order'));
  },

  /** POST /orders → 201 */
  open(req: OpenOrderRequest): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orders(), req), 'Could not open the order'));
  },

  /** PUT /orders/{id}/covers */
  setCovers(id: string, covers: number): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.put(dineEndpoints.orderCovers(id), { covers })));
  },

  /** PUT /orders/{id}/seating */
  setSeating(id: string, req: SeatingRequest): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.put(dineEndpoints.orderSeating(id), req)));
  },

  /** PUT /orders/{id}/avoid — replaces the list */
  setAvoid(id: string, avoid: string[]): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.put(dineEndpoints.orderAvoid(id), { avoid })));
  },

  /** POST /orders/{id}/lines → 201 */
  addLine(id: string, req: AddLineRequest): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orderLines(id), req)));
  },

  /** PUT /orders/{id}/lines/{lineId} — unsent lines only */
  updateLine(id: string, lineId: string, req: UpdateLineRequest): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.put(dineEndpoints.orderLine(id, lineId), req)));
  },

  /** DELETE /orders/{id}/lines/{lineId} — unsent only (soft delete) */
  removeLine(id: string, lineId: string): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.delete(dineEndpoints.orderLine(id, lineId))));
  },

  /** POST /orders/{id}/lines/open-item → 201 */
  addOpenItem(id: string, req: OpenItemRequest): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orderOpenItem(id), req)));
  },

  /** POST /orders/{id}/lines/{lineId}/approve */
  approveLine(id: string, lineId: string): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orderLineApprove(id, lineId))));
  },

  /** POST /orders/{id}/lines/charge → 201 */
  addCharge(id: string, req: ChargeLineRequest): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orderCharge(id), req)));
  },

  /** POST /orders/{id}/lines/{lineId}/cancel */
  cancelLine(id: string, lineId: string, reason?: string | null): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(
      apiClient.post(dineEndpoints.orderLineCancel(id, lineId), { reason: reason ?? null }),
    ));
  },

  /** POST /orders/{id}/lines/{lineId}/return — a served dish sent back; the message says what happens next. */
  async returnLine(id: string, lineId: string, req: ReturnLineRequest): Promise<{ order: OrderDetailDto; message: string }> {
    let message = 'Dish returned';
    const order = await detail(
      call<OrderDetailDto>(
        apiClient.post(dineEndpoints.orderLineReturn(id, lineId), req).then((res) => {
          message = (res.data as { message?: string } | undefined)?.message ?? message;
          return res;
        }),
        'Could not return the dish',
      ),
    );
    return { order, message };
  },

  /** POST /orders/{id}/returns/{returnId}/approve — a captain lets it be complimentary. */
  approveReturn(id: string, returnId: string): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orderReturnApprove(id, returnId))));
  },

  /** POST /orders/{id}/returns/{returnId}/reject — a captain charges it after all. */
  rejectReturn(id: string, returnId: string, reason: string): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orderReturnReject(id, returnId), { reason })));
  },

  /** GET /orders/approvals — what waits for a captain in this outlet, oldest first. */
  approvals(): Promise<PendingApprovalDto[]> {
    return call<PendingApprovalDto[]>(apiClient.get(dineEndpoints.approvals()), 'Could not load approvals');
  },

  /** POST /orders/{id}/send → SendResultDto (idempotent per round) */
  send(id: string): Promise<SendResultDto> {
    return call<SendResultDto>(apiClient.post(dineEndpoints.orderSend(id)), 'Could not send to the kitchen').then(
      hydrateSendResult,
    );
  },

  /** POST /orders/{id}/hold — records a deliberate park */
  hold(id: string): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orderHold(id))));
  },

  /** POST /orders/{id}/cancel — only while the order has no KOT */
  cancel(id: string, reason: string): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orderCancel(id), { reason })));
  },

  /** POST /orders/{id}/move */
  move(id: string, toTableId: string): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orderMove(id), { toTableId })));
  },

  /** POST /orders/{id}/merge → the TARGET order */
  merge(id: string, intoOrderId: string): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orderMerge(id), { intoOrderId })));
  },

  /** POST /orders/{id}/request-bill */
  requestBill(id: string, req: RequestBillRequest): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orderRequestBill(id), req)));
  },

  /** POST /orders/{id}/close — retired with cycle 4; the API answers 409. Kept for the contract only. */
  close(id: string): Promise<OrderDetailDto> {
    return detail(call<OrderDetailDto>(apiClient.post(dineEndpoints.orderClose(id))));
  },
};
